/**
 * Single source of truth for terms acceptance copy.
 * Reused for signup checkbox and post-login acceptance screen to avoid legal copy drift.
 */

export const TERMS_ACCEPTANCE_COPY =
  "By creating an account, you agree to the Terms of Service and Privacy Policy, and acknowledge that Watt Tower provides informational monitoring and advisory insights only. You retain full responsibility for operating and monitoring your energy systems.";

export const TERMS_ACCEPTANCE_URL_TERMS = "/terms";
export const TERMS_ACCEPTANCE_URL_PRIVACY = "/privacy";

const TERMS_LABEL = "Terms of Service";
const PRIVACY_LABEL = "Privacy Policy";

/**
 * Splits the copy by the two link phrases (keeping them) so we can render links.
 * Order matters: split by first phrase, then second, to get consistent segments.
 */
function getAcceptanceCopySegments(): (string | { type: "terms" | "privacy"; label: string })[] {
  const segments: (string | { type: "terms" | "privacy"; label: string })[] = [];
  let remaining = TERMS_ACCEPTANCE_COPY;
  const phrases: [string, "terms" | "privacy"][] = [
    [TERMS_LABEL, "terms"],
    [PRIVACY_LABEL, "privacy"],
  ];
  for (const [phrase, type] of phrases) {
    const idx = remaining.indexOf(phrase);
    if (idx === -1) continue;
    segments.push(remaining.slice(0, idx));
    segments.push({ type, label: phrase });
    remaining = remaining.slice(idx + phrase.length);
  }
  if (remaining) segments.push(remaining);
  return segments;
}

interface TermsAcceptanceLabelProps {
  className?: string;
  linkClassName?: string;
}

/**
 * Renders TERMS_ACCEPTANCE_COPY with "Terms of Service" and "Privacy Policy" as links (new tab).
 * Use for signup checkbox and acceptance screen.
 */
export function TermsAcceptanceLabel({ className, linkClassName }: TermsAcceptanceLabelProps) {
  const segments = getAcceptanceCopySegments();
  return (
    <span className={className}>
      {segments.map((seg, i) => {
        if (typeof seg === "string") return <span key={i}>{seg}</span>;
        const url = seg.type === "terms" ? TERMS_ACCEPTANCE_URL_TERMS : TERMS_ACCEPTANCE_URL_PRIVACY;
        return (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
          >
            {seg.label}
          </a>
        );
      })}
    </span>
  );
}
